// src/types/entities.ts - Types multi-tenant avec champs transaction

import { createI18nLabelMap } from '../utils/i18n/getStatusLabel';
import type { SaleLineLifecycleStatus } from './saleCorrection';

  // Employee avec businessId et profil de rôle
  export interface Employee {
    id: string;
    businessId: string;  // ✅ MODIFIÉ : institutId → businessId
    name: string;
    role: string;  // ✅ NOUVEAU : Rôle métier dans le business (obligatoire, ex: "Esthéticienne", "Caissier")
    roleProfileId?: string; // ✅ MODIFIÉ : Profil de permissions (optionnel, pour les employés avec compte utilisateur)
    userId?: string;  // ✅ NOUVEAU : Lien vers le user (si créé par Super Admin)
    email?: string;
    phone?: string;
    isActive: boolean;
    /**
     * Lieu de travail par défaut pour ce business (vente multi-lieux / any_site).
     * Doit appartenir à `assignedSiteIds` lorsque cette liste est non vide.
     * Chaque document employee est déjà scopé à un `businessId` ;
     * un user multi-business a un employeeId distinct par établissement.
     */
    defaultSiteId?: string;
    /**
     * Lieux de vente autorisés (1..N). Vide / absent = pas de restriction (comportement historique).
     * Lecture rétrocompat : si absent mais `defaultSiteId` présent → traité comme `[defaultSiteId]`.
     */
    assignedSiteIds?: string[];
    baseSalary?: number;  // 🆕 Salaire mensuel de base en FCFA
    /**
     * Informations de paiement facultatives (IBAN, Orange Money, MTN, nom du compte, etc.).
     * Texte libre pour le règlement des salaires.
     */
    paymentInfo?: string;
    /** Montant variable prévu (prime objectifs) — configuré sur la fiche employé, recopié sur les objectifs mensuels */
    plannedVariableAmount?: number;
    /** Exclu de la liste employés côté client si true */
    isSuperAdmin: boolean;
    /** Exclu de la liste employés côté client si true */
    isCSM: boolean;
    /** Configuration de validation des rapports d'activité (par employé) */
    activityReportConfig?: {
      validationMode: 'daily' | 'weekly' | 'monthly';
      /**
       * Mode weekly uniquement : jour de la semaine (ISO) jusqu'auquel valider.
       * Défaut : sunday.
       */
      weeklyDeadlineDay?:
        | 'monday'
        | 'tuesday'
        | 'wednesday'
        | 'thursday'
        | 'friday'
        | 'saturday'
        | 'sunday';
      /** Heure max de validation (0–23). Défaut 23. */
      deadlineHour?: number;
    };
  }

  // ✅ NOUVEAU : Catégorie de service configurable par le client
  export interface ServiceCategory {
    id: string;
    businessId: string;
    name: string;         // "Consultations", "Ventes", "Réparations", etc.
    description?: string;
    color: string;        // Couleur pour l'affichage (#1890ff, #52c41a, etc.)
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date;
  }

  
  // ✅ NOUVEAU : Produit consommé par un service (configuration)
  export interface ServiceConsumedProduct {
    productId: string;      // ID du produit
    productName: string;     // Nom dénormalisé (pour affichage)
    quantity: number;        // Quantité consommée (doit être > 0)
  }

export type ServiceStockConsumptionMode = 'direct' | 'manufacturing_order' | 'none';

export interface ManufacturingTemplateLine {
  productId: string;
  productName: string;
  quantity: number;
  /** Lieu de prélèvement — obligatoire pour chaque ligne utile du modèle OF (contrôlé à la sauvegarde). */
  siteId?: string;
  /** Dénomination du lieu (pour affichage et traçabilité des mouvements). */
  siteName?: string;
  note?: string;
}

export interface ManufacturingTemplate {
  /** Lignes par défaut (prévision / suggestion) pour initialiser un ordre. */
  defaultLines: ManufacturingTemplateLine[];
  instructions?: string;
  estimatedDurationMinutes?: number;
}

  // Service avec businessId et catégorie dynamique
  export interface Service {
    id: string;
    businessId: string;  // ✅ MODIFIÉ : institutId → businessId
    name: string;
    basePrice: number;
    categoryId: string;  // ✅ NOUVEAU : Référence à ServiceCategory
    description?: string;

    /**
     * Mode de tarification.
     * - fixed (défaut) : prix forfaitaire (basePrice)
     * - per_kg : basePrice = prix au kilo ; quantité saisie en caisse
     */
    pricingMode?: 'fixed' | 'per_kg';
    /** Libellé d'unité si per_kg (défaut "kg"). */
    unitLabel?: string;

    // ✅ NOUVEAU : Champs TVA  
    taxRateId?: string;           // ID du taux (référence)
    priceIncludesTax?: boolean;   // Mode de saisie
    
    // ✅ NOUVEAU : Quota référent (optionnel, affiché uniquement si feature referents activée)
    quotaPercentage?: number;     // Pourcentage du quota attribué au référent (0-100%)
    
    // ✅ NOUVEAU : Produits consommés lors de la vente de ce service (optionnel)
    /**
     * Produits consommés lors de la vente de ce service.
     * Si vide ou undefined, la prestation ne consomme aucun produit (rétrocompatibilité).
     */
    consumedProducts?: ServiceConsumedProduct[];

  /**
   * Mode de consommation de stock.
   * - direct: décrémentation à la vente depuis consumedProducts
   * - manufacturing_order: création d'un ordre de fabrication, consommation réelle saisie dans l'ordre
   * - none: aucune consommation stock
   *
   * Si absent, on considère 'direct' si consumedProducts non vide, sinon 'none' (rétrocompatibilité).
   */
  stockConsumptionMode?: ServiceStockConsumptionMode;

  /** Template de fabrication utilisé si stockConsumptionMode === 'manufacturing_order'. */
  manufacturingTemplate?: ManufacturingTemplate;
  }
  
  // Client avec businessId
  export interface Client {
    id: string;
    businessId: string;  // ✅ MODIFIÉ : institutId → businessId
    name: string;
    phone: string;
    email?: string;
    birthDate?: Date;
    loyaltyBalance: number;
    
    address?: string;           // Complément d'adresse (rue, repère…)
    city?: string;              // Ville
    district?: string;          // Quartier
    gender?: 'male' | 'female' | 'other';  // Genre
    emergencyContact?: {         // Contact d'urgence
      name: string;
      phone: string;
      relationship: string;      // "Père", "Mère", "Conjoint", etc.
    };
    preferences?: {              // Préférences client
      preferredLanguage?: string; // Langue préférée
      communicationMethod?: 'phone' | 'email' | 'sms'; // Méthode de contact préférée
      appointmentReminders?: boolean; // Souhaite des rappels de RDV
      marketingOptIn?: boolean; // Consentement messages marketing / WhatsApp
    };
    /** Type de client (optionnel), libellé de la liste configurée au business */
    clientType?: string;
    /** Date de dernière visite (dernière transaction : prestation ou vente) */
    lastVisitDate?: Date;
    /**
     * Id Pass BonSpot (bonspotPasses) lié à ce client via téléphone.
     * Renseigné après ensurePassForPhone / activation.
     */
    passId?: string;
    createdAt: Date;
    updatedAt: Date;
  }
  
  export type PrestationPaymentMethod = 'espece' | 'orange_money' | 'mtn_mobile_money'
  
  export const PAYMENT_METHOD_LABELS: Record<PrestationPaymentMethod, string> = createI18nLabelMap(
    'paymentMethod',
    ['espece', 'orange_money', 'mtn_mobile_money'] as const
  );
  
  export const PAYMENT_METHOD_COLORS: Record<PrestationPaymentMethod, string> = {
    espece: 'green', 
    orange_money: 'orange',
    mtn_mobile_money: 'yellow'
  }

  // ✅ NOUVEAU : Support pour plusieurs moyens de paiement par transaction
  export interface PaymentSplit {
    method: PrestationPaymentMethod;
    amount: number; // Montant payé avec ce moyen
    /** URLs Storage des justificatifs (Orange Money / MTN) — obligatoires en caisse si montant > 0 */
    attachments?: string[];
  }

  // ✅ NOUVEAU : Types pour les dépenses (même moyens de paiement que transactions POS)
  export interface Expense {
    id: string;
    businessId: string;
    category: string; // ✅ MODIFIÉ : string au lieu de ExpenseCategory pour supporter les catégories personnalisées
    description: string;
    amount: number;
    amountHT: number;
    taxAmount: number;
    taxRateId?: string;
    date: Date;
    paymentMethod: PrestationPaymentMethod;
    supplier?: string;
    reference?: string; // Numéro de facture, référence
    /** Si la dépense a été générée depuis une fiche de paie (traçabilité / annulation validation). */
    sourcePayrollId?: string;
    attachments?: string[]; // URLs des justificatifs
    createdAt: Date;
    updatedAt: Date;
  }

  // ✅ NOUVEAU : Note de frais (soumise par employé, validée par manager)
  export type ExpenseNoteStatus = 'pending' | 'validated' | 'rejected';

  export interface ExpenseNote {
    id: string;
    businessId: string;

    employeeId: string;
    employeeName: string;

    // Catégorie partagée avec Dépenses (config business)
    category: string;

    description: string;
    amount: number;
    date: Date;
    attachments?: string[];

    status: ExpenseNoteStatus;

    createdAt: Date;
    createdBy: string;

    validatedAt?: Date;
    validatedBy?: string;

    rejectedAt?: Date;
    rejectedBy?: string;
    rejectionReason?: string;

    // Liens de traçabilité
    expenseId?: string;
    salaryAdjustmentId?: string;
    appliedMonth?: number;
    appliedYear?: number;
    /** true si le remboursement paie a été reporté car le mois de la note était fermé */
    payrollDeferred?: boolean;
    payrollDeferredSkippedMonths?: number;

    /** Masquée de la liste par défaut ; le document reste en base (pas de suppression à la validation). */
    archived?: boolean;
    archivedAt?: Date;
  }

  // ✅ NOUVEAU : Indicateur de performance (objectifs mensuels + part variable)
  export type PerformanceObjectivesStatus = 'draft' | 'validated';

  /** Unité d’expression du quota / de la quantité (affichage + cohérence métier) */
  export type PerformanceObjectiveQuantityKind = 'number' | 'fcfa';

  export type PerformanceObjectiveItemStatus = 'pending' | 'validated';

  /** Type de ligne — absent / legacy = quantitative */
  export type PerformanceObjectiveItemKind = 'quantitative' | 'score' | 'boolean';

  export interface PerformanceObjectiveItem {
    id: string;
    label: string;
    /** Lien catalogue (optionnel — ligne libre quantitative autorisée) */
    criteriaId?: string;
    /** Absent = quantitative (rétrocompat) */
    kind?: PerformanceObjectiveItemKind;
    /** Nombre (unité) ou montant FCFA (optionnel sur anciens enregistrements) */
    quantityKind?: PerformanceObjectiveQuantityKind;
    /** Objectif atteint ou non (saisi manuellement ; kind quantitative) */
    objectiveMet?: boolean;
    status?: PerformanceObjectiveItemStatus; // rétrocompat : aligné sur objectiveMet lors de l’enregistrement
    weightPct: number; // totalise 100% sur le mois
    /** Quota (objectif) — quantitative */
    targetValue: number;
    /** Quantité réalisée — quantitative */
    achievedValue: number;
    /** kind === 'score' */
    scoreValue?: number;
    scoreMin?: number;
    scoreMax?: number;
    /** kind === 'boolean' */
    booleanValue?: boolean;
    /** Computed: achievedValue/targetValue (0-100+) — quantitative */
    achievementPctComputed: number;
    /** @deprecated préférer quantityKind */
    unit?: string;
  }

  export interface PerformanceObjectives {
    id: string;
    businessId: string;

    employeeId: string;
    employeeName: string;

    month: number; // 1-12
    year: number;  // ex 2026

    /**
     * Montant de prime du mois (source de vérité, éditable sur la fiche).
     * Suggestion initiale possible depuis Employee.plannedVariableAmount.
     */
    variableAmount: number;
    /** Snapshot optionnel du plannedVariableAmount employé (audit, ne pas écraser variableAmount) */
    plannedVariableAmountSnapshot?: number;

    objectives: PerformanceObjectiveItem[];

    status: PerformanceObjectivesStatus;

    createdAt: Date;
    createdBy: string;
    updatedAt: Date;
    updatedBy: string;

    validatedAt?: Date;
    validatedBy?: string;

    /** Liens de traçabilité (prime variable) */
    salaryAdjustmentId?: string;
    variablePayAmount?: number;
    appliedMonth?: number;
    appliedYear?: number;
  }

  // ✅ MODIFIÉ : Seulement 2 catégories système (liées à d'autres features)
  export type ExpenseCategory = 
    | 'salary'           // Salaires (lié à la feature Paie)
    | 'delivery';         // Livraison (lié à la feature Approvisionnement)

  // ✅ MODIFIÉ : Seulement 2 catégories système
  export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = createI18nLabelMap(
    'expenseCategory',
    ['salary', 'delivery'] as const
  );

  export const EXPENSE_CATEGORY_COLORS: Record<ExpenseCategory, string> = {
    salary: 'blue',
    delivery: 'green'
  };

  /** Libellés pour l'affichage du moyen de paiement (dépenses + transactions). Inclut les 3 moyens POS + anciennes valeurs pour rétrocompat. */
  export const EXPENSE_PAYMENT_METHOD_LABELS: Record<string, string> = createI18nLabelMap(
    'expensePaymentMethod',
    [
      'espece',
      'orange_money',
      'mtn_mobile_money',
      'cash',
      'bank_transfer',
      'check',
      'credit_card',
      'mobile_money',
      'other',
    ] as const
  );
  
  // ✅ NOUVEAU : Produit effectivement consommé lors d'une prestation (historique)
  export interface PrestationConsumedProduct {
    productId: string;
    productName: string;
    quantity: number;
    unitCost?: number;      // Coût unitaire au moment de la consommation (optionnel, pour traçabilité)
    stockBefore: number;    // Stock avant déduction (pour traçabilité)
    stockAfter: number;      // Stock après déduction (pour traçabilité)
  siteId?: string;
  siteName?: string;
  }

  // Prestation avec businessId ET champs transaction
  export interface Prestation {
    id: string;
    businessId: string;    // ✅ MODIFIÉ : institutId → businessId
    employeeId: string;
    employeeName: string;
    serviceId: string;
    serviceName: string;
    clientId?: string;
    clientName: string;
    price: number;
    /** Quantité (kg si pricingMode per_kg). Défaut 1. */
    quantity?: number;
    /** Prix unitaire (forfait ou prix/kg). */
    unitPrice?: number;
    pricingMode?: 'fixed' | 'per_kg';
    date: Date;
    notes?: string;
    paymentMethod: PrestationPaymentMethod; // ⚠️ DÉPRÉCIÉ : Utiliser paymentSplits à la place
    paymentSplits?: PaymentSplit[]; // ✅ NOUVEAU : Support pour plusieurs moyens de paiement
    loyaltyBonusGranted?: number;
    loyaltyAmountUsed?: number;
    finalPrice: number;
    isArchived: boolean;
    archivedAt?: Date;
    archiveReason?: string;

    /**
     * Cycle de vie (feature saleCorrections). Absent = dérivé de isArchived
     * (active si !isArchived, voided si archivé legacy).
     */
    lifecycleStatus?: SaleLineLifecycleStatus;
    correctionEventIds?: string[];
    supersededByTransactionGroupId?: string;
    supersedesTransactionGroupId?: string;
    
    // Champs promotion
    promotionId?: string;
    promotionDiscount?: number;

    /** Prix catalogue (basePrice) au moment de la vente. */
    listUnitPrice?: number;
    /** Remise commerciale TTC ligne (catalogue − prix saisi) × qté. */
    commercialDiscount?: number;
    /** Motif de la remise commerciale (obligatoire si commercialDiscount &gt; 0). */
    commercialDiscountReason?: string;
    /** Remise Pass BonSpot du ticket (dénormalisée — ne pas sommer par ligne). */
    passDiscountTotal?: number;
    /** Libellé explicite Pass (ex. « M1 · −10 % »). */
    passDiscountLabel?: string;
    
    // Champs transaction
    transactionGroupId?: string;
    transactionIndex?: number;
    receiptNumber: string;
    idempotencyKey?: string;
    
    // ✅ NOUVEAUX : Champs TVA pour la comptabilité
    taxRateId?: string;           // ID du taux de TVA appliqué
    taxRateName?: string;         // Nom du taux (ex: "TVA Standard")
    taxRate?: number;             // Pourcentage du taux (ex: 19.25)
    subtotalExcludingTax?: number; // Montant HT
    totalTaxAmount?: number;       // Montant de la TVA
    subtotalIncludingTax?: number; // Montant TTC
    
    // ✅ NOUVEAUX : Champs référent
    referentId?: string;           // ID du référent (médecin)
    referentName?: string;         // Nom du référent (dénormalisé)
    
    // ✅ NOUVEAU : Produits effectivement consommés lors de cette prestation (optionnel)
    /**
     * Produits effectivement consommés lors de cette prestation.
     * Rempli automatiquement lors de la création si le service a des consumedProducts.
     */
    consumedProducts?: PrestationConsumedProduct[];
    
    // ✅ NOUVEAU : Champs pour plan de paiement (échelonné)
    paymentPlanId?: string;           // ID du plan de paiement lié
    paymentStatus?: 'unpaid' | 'partial' | 'paid'; // Statut de paiement
    amountPaid?: number;               // Montant déjà payé (pour les plans)
    amountRemaining?: number;          // Montant restant à payer
    isDelivered?: boolean;             // Prestation terminée/remise (équivalent à isDelivered pour produits)
    deliveredAt?: Date;                // Date de remise/terminaison

    /** Lieu de vente (mode any_site / multi-lieux) — dénormalisé à la caisse. */
    siteId?: string;
    siteName?: string;

    /** Pack catalogue (si vendu en forfait). */
    packId?: string;
    packInstanceId?: string;
    packName?: string;
    packPrice?: number;
    packComponentIndex?: number;
    soldAsPack?: boolean;
  }

  
  export const LOYALTY_USAGE_MINIMUM = 1000;
  
  export interface DailyStats {
    date: string;
    totalRevenue: number;
    totalServices: number;
    employeeStats: {
      [employeeId: string]: {
        name: string;
        revenue: number;
        services: number;
      };
    };
  }
  
  export interface MonthlyStats {
    month: string;
    year: number;
    totalRevenue: number;
    totalServices: number;
    averageDailyRevenue: number;
    topEmployee: {
      id: string;
      name: string;
      revenue: number;
    };
    topService: {
      id: string;
      name: string;
      count: number;
    };
  }
  
  export interface Appointment {
    id: string;
    businessId: string;  // ✅ MODIFIÉ : institutId → businessId
    
    // Client
    clientId?: string;
    clientName: string;
    clientPhone: string;
    
    // Service et employé
    serviceIds?: string[];
    serviceNames?: string[];
    employeeId?: string;
    employeeName?: string;
    
    // Date et heure
    date: Date;
    startTime: string; // Format "HH:mm" (ex: "14:30")
    duration?: number; // En minutes, optionnel
    
    // Détails du RDV
    title: string; // Raison courte
    description?: string; // Description détaillée
    
    // Statut
    status: AppointmentStatus;
    
    // Métadonnées
    createdAt: Date;
    createdBy: string; // ID de l'utilisateur qui a créé
    notes?: string;
    /** Lien vers une demande de ramassage (passage planifié). */
    pickupRequestId?: string;
  }
  
  export type AppointmentStatus = 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  
  export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = createI18nLabelMap(
    'appointmentStatus',
    ['scheduled', 'confirmed', 'completed', 'cancelled'] as const
  );
  
  export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
    scheduled: 'orange',
    confirmed: 'blue',
    completed: 'green', 
    cancelled: 'red'
  };

  // Interface pour les transactions (ventes)
  export interface Transaction {
    id: string;
    businessId: string;
    clientId: string;
    items: {
      type: 'service' | 'product';
      id: string;
      name: string;
      price: number;
      quantity: number;
    }[];
    totalAmount: number;
    paymentMethod: 'cash' | 'card' | 'transfer';
    createdAt: Date;
    updatedAt: Date;
  }

export type TransactionFollowUpStatus =
  | 'pending_delay'
  | 'ready'
  | 'sent'
  | 'skipped_no_phone'
  | 'skipped_opt_out'
  | 'suppressed_24h'
  | 'cancelled';

export interface TransactionFollowUpMessage {
  id: string;
  businessId: string;
  transactionGroupId: string;
  receiptNumber: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  transactionType: 'services' | 'products' | 'mixed';
  transactionAt: Date;
  eligibleAt: Date;
  status: TransactionFollowUpStatus;
  messageText: string;
  sentAt?: Date;
  sentBy?: string;
  skippedReason?: string;
  createdAt: Date;
  updatedAt: Date;
}

  // ✅ NOUVEAU : Ajustements salariaux (avances, absences, primes, etc.)
  export interface SalaryAdjustment {
    id: string;
    businessId: string;
    employeeId: string;
    employeeName: string;
    
    // Type d'ajustement
    type: SalaryAdjustmentType;
    
    // Montant (toujours positif, le type détermine si c'est ajout ou déduction)
    amount: number;
    isDeduction: boolean;              // true = déduction, false = ajout
    
    // Période concernée
    month: number;                     // 0-11 (Janvier = 0)
    year: number;
    monthLabel: string;                // "Octobre 2025"
    
    // Description
    description: string;               // "Avance sur salaire", "Absence 3 jours", etc.
    
    // Détails pour absences/retards
    daysCount?: number;                // Nombre de jours (si absence)
    startDate?: Date;                  // Date début (si absence)
    endDate?: Date;                    // Date fin (si absence)
    
    // Statut et workflow
    status: AdjustmentStatus;
    
    // Approbation
    requestDate: Date;
    approvedDate?: Date;
    approvedBy?: string;
    rejectedDate?: Date;
    rejectedBy?: string;
    rejectionReason?: string;
    
    // Application dans la paie
    payrollId?: string;                // Référence à la paie où c'est appliqué
    appliedAt?: Date;                  // Quand c'est appliqué dans la paie

    /** Renseigné si l’ajustement provient de la validation d’une note de frais (traçabilité). */
    expenseNoteId?: string;
    
    notes?: string;
    createdAt: Date;
    createdBy: string;
  }

  export type SalaryAdjustmentType = 
    | 'advance'       // Avance sur salaire
    | 'absence'       // Absence non payée
    | 'late'          // Retards
    | 'penalty'       // Pénalité
    | 'bonus'         // Prime
    | 'other';        // Autre

  export type AdjustmentStatus = 
    | 'pending'       // En attente d'approbation
    | 'approved'      // Approuvé, en attente d'application
    | 'rejected'      // Rejeté
    | 'applied';      // Appliqué dans une paie

  export const SALARY_ADJUSTMENT_TYPE_LABELS: Record<SalaryAdjustmentType, string> = createI18nLabelMap(
    'salaryAdjustmentType',
    ['advance', 'absence', 'late', 'penalty', 'bonus', 'other'] as const
  );

  export const SALARY_ADJUSTMENT_TYPE_COLORS: Record<SalaryAdjustmentType, string> = {
    advance: 'orange',
    absence: 'red',
    late: 'volcano',
    penalty: 'magenta',
    bonus: 'green',
    other: 'blue',
  };

  export const ADJUSTMENT_STATUS_LABELS: Record<AdjustmentStatus, string> = createI18nLabelMap(
    'adjustmentStatus',
    ['pending', 'approved', 'rejected', 'applied'] as const
  );

  export const ADJUSTMENT_STATUS_COLORS: Record<AdjustmentStatus, string> = {
    pending: 'orange',
    approved: 'green',
    rejected: 'red',
    applied: 'blue',
  };

  // ✅ NOUVEAU : Fiche de paie mensuelle
  export interface Payroll {
    id: string;
    businessId: string;
    employeeId: string;
    employeeName: string;
    
    // Période
    month: number;                     // 0-11
    year: number;
    monthLabel: string;                // "Octobre 2025"
    periodStart: Date;                 // 1er du mois
    periodEnd: Date;                   // Dernier jour du mois
    
    // Rémunération
    baseSalary: number;                // Salaire de base du mois
    
    // Ajustements appliqués
    adjustments: PayrollAdjustment[];
    totalAdjustments: number;          // Somme algébrique (positif - négatif)
    
    // Calcul final
    grossSalary: number;               // Salaire brut (base + bonus)
    totalDeductions: number;           // Total déductions (avances + absences + pénalités)
    netSalary: number;                 // Salaire net à payer
    
    // Statut
    status: PayrollStatus;
    
    // Paiement
    paymentDate?: Date;
    paymentMethod?: string;
    paidBy?: string;
    
    // Métadonnées
    notes?: string;
    createdAt: Date;
    createdBy: string;
    validatedAt?: Date;
    validatedBy?: string;

    /** ID document `expenses` créé à la validation (suppression à l’annulation validation si présent). */
    salaryExpenseId?: string;
  }

  export interface PayrollAdjustment {
    adjustmentId: string;              // Référence au SalaryAdjustment
    type: SalaryAdjustmentType;
    description: string;
    amount: number;
    isDeduction: boolean;
  }

  export type PayrollStatus = 
    | 'draft'         // Brouillon (généré automatiquement)
    | 'validated'     // Validé par le manager
    | 'paid';         // Payé

  export const PAYROLL_STATUS_LABELS: Record<PayrollStatus, string> = createI18nLabelMap(
    'payrollStatus',
    ['draft', 'validated', 'paid'] as const
  );

  export const PAYROLL_STATUS_COLORS: Record<PayrollStatus, string> = {
    draft: 'orange',
    validated: 'blue',
    paid: 'green',
  };
